// src/pages/EmailsPage.tsx

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useGetThreadsQuery, useGetMessagesQuery, useUpdateThreadMutation, useToggleMessageFavoriteMutation, useMarkThreadAsReadMutation, useSendMessageMutation, useGetEmailProvidersQuery, useSendChatMessageMutation, useCreateThreadMutation } from '../api/uniboxApi';
import { useGetFoldersQuery, useSaveMessageToFolderMutation, useCreateFolderMutation } from '../api/messageFoldersApi';
import ThreadAISummary from '../components/messaging/ThreadAISummary';
import ThreadCollaborationHeader from '../components/messaging/ThreadCollaborationHeader';
import PinMessageButton from '../components/messaging/PinMessageButton';
import MessageReactions from '../components/messaging/MessageReactions';
import EmailMessageBody from '../components/messaging/EmailMessageBody';
import { stripQuotedEmailForDisplay } from '../utils/emailDisplay';
import CollaborationSignals from '../components/messaging/CollaborationSignals';
import NewChatModal from '../components/messaging/NewChatModal';
import { useMessagePermissions } from '../hooks/useMessagePermissions';
import ErrorState from '../components/common/ErrorState';
import NewSmsModal from '../components/messaging/NewSmsModal';
import SystemChannelReplyComposer from '../components/messaging/SystemChannelReplyComposer';
import { isSystemChannelThread } from '../components/messaging/isSystemChannelThread';
import { useCollaborationSignals } from '../hooks/useCollaborationSignals';
import { useMessageRealtime } from '../hooks/useMessageRealtime';
import { Link, useSearchParams } from 'react-router-dom';
import type { CommunicationThread, CommunicationMessage, MessageMedium, ContactType } from '../types/api';
import { useGetCurrentUserQuery } from '../api/authApi';
import WeOpenInboxPanel from '../components/weopen/WeOpenInboxPanel';

// 'weopen' is a DISTINCT surface, not a medium filter. It is deliberately not a
// `PrimaryMediumFilter` value: those drive the CRM thread query, and WeOpen
// conversations come live from the BFF, never from CRM's tables.
type ActiveTab = 'unibox' | 'compose' | 'weopen';
type PrimaryMediumFilter = 'all' | 'email' | 'sms' | 'chat';

const mediumLabels: Record<MessageMedium, string> = {
  email: 'Email',
  sms: 'SMS',
  in_app: 'In-App',
  whatsapp: 'WhatsApp',
  call_log: 'Call',
  chat: 'Chat',
  call: 'Call',
  voicemail: 'Voicemail',
  other: 'Other',
};

const mediumIcons: Record<MessageMedium, string> = {
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  sms: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  in_app: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  whatsapp: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
  call_log: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  call: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  voicemail: 'M3 8a5 5 0 015-5h8a5 5 0 110 10H8a5 5 0 01-5-5zm5 3a3 3 0 100-6 3 3 0 000 6zm8 0a3 3 0 100-6 3 3 0 000 6z',
  other: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
};

export default function EmailsPage() {
  // Capability-driven, never role-driven. Both are false while the Central
  // context is unresolved, so no control flashes enabled during boot and the
  // page never renders thread data before read authority is known.
  const { canViewMessages, canWriteMessages } = useMessagePermissions();

  // Read URL search params for deep-linking from command palette and notifications
  const [searchParams] = useSearchParams();
  const urlUnreadOnly = searchParams.get('unreadOnly') === 'true';
  const urlMedium = searchParams.get('medium') as PrimaryMediumFilter | 'call' | null;
  const urlThreadId = searchParams.get('threadId');
  const urlMessageId = searchParams.get('messageId');

  const [activeTab, setActiveTab] = useState<ActiveTab>('unibox');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(urlThreadId);
  const [primaryMediumFilter, setPrimaryMediumFilter] = useState<PrimaryMediumFilter>(
    urlMedium === 'email' || urlMedium === 'sms' || urlMedium === 'chat' ? urlMedium : 'all'
  );
  const [unreadOnly, setUnreadOnly] = useState(urlUnreadOnly);
  const [showArchived, setShowArchived] = useState(false);
  const [showFolders, setShowFolders] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  /** Medium selector for unified threads. Initialized from URL ?medium= param
   *  (set by contact detail "SMS" / "Email" quick action buttons). */
  const [replyMedium, setReplyMedium] = useState<'email' | 'sms' | 'in_app'>(
    urlMedium === 'sms' ? 'sms' : urlMedium === 'email' ? 'email' : 'in_app'
  );
  const [folderMessageId, setFolderMessageId] = useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNewSmsModal, setShowNewSmsModal] = useState(false);
  // Reply-to-message state (for threaded replies)
  const [replyToMessageId, setReplyToMessageId] = useState<string | null>(null);

  // Message refs for scroll-to-message functionality
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Get current user for chat
  const { data: currentUser } = useGetCurrentUserQuery();

  // Get active workspace
  const activeWorkspace = useActiveWorkspace();

  // Fetch threads with unified messaging filters
  const { data: threadsData, isLoading: isLoadingThreads } = useGetThreadsQuery(
    {
      workspaceId: activeWorkspace?.workspace.id,
      primaryMedium: primaryMediumFilter !== 'all'
        ? primaryMediumFilter as any
        : undefined,
      unreadOnly: unreadOnly || undefined,
      isArchived: showArchived,
      q: searchQuery || undefined,
    },
    {
      // Skipped on the WeOpen surface: that pane reads the BFF, so querying CRM
      // threads there would be a wasted request against the wrong source.
      skip: !activeWorkspace || activeTab === 'weopen',
      // Poll every 15s so inbound emails appear without manual reload
      pollingInterval: 15_000,
    }
  );

  // Fetch messages for selected thread
  const { data: messagesData, refetch: refetchMessages } = useGetMessagesQuery(
    { threadId: selectedThreadId! },
    {
      skip: !selectedThreadId,
      // Poll every 10s so inbound replies appear in the open thread
      pollingInterval: 10_000,
    }
  );

  // Mark thread as read mutation
  const [markThreadAsRead] = useMarkThreadAsReadMutation();

  // Fetch folders
  const { data: folders } = useGetFoldersQuery(
    { workspaceId: activeWorkspace?.workspace.id },
    { skip: !activeWorkspace }
  );

  const [updateThread] = useUpdateThreadMutation();
  const [toggleFavorite] = useToggleMessageFavoriteMutation();
  const [saveToFolder] = useSaveMessageToFolderMutation();
  const [createFolder] = useCreateFolderMutation();

  // Collaboration signals for typing indicator
  const { startTyping, stopTyping } = useCollaborationSignals({
    workspaceId: activeWorkspace?.workspace.id || '',
    threadId: selectedThreadId || '',
    enabled: !!activeWorkspace?.workspace.id && !!selectedThreadId,
  });
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [sendChatMessage, { isLoading: isSendingChat }] = useSendChatMessageMutation();
  const [createThread] = useCreateThreadMutation();

  // Compose form state
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeSuccess, setComposeSuccess] = useState(false);

  // Check email provider availability
  const { data: emailProviders } = useGetEmailProvidersQuery();
  const hasEmailProvider = emailProviders && emailProviders.length > 0;

  // Subscribe to realtime updates for threads and messages
  useMessageRealtime({
    workspaceId: activeWorkspace?.workspace.id || '',
  });

  const selectedThread = useMemo(() => {
    if (!selectedThreadId || !threadsData) return null;
    return threadsData.threads.find((t) => t.id === selectedThreadId) || null;
  }, [selectedThreadId, threadsData]);

  // Auto-set the reply medium when switching threads — default to the
  // thread's primary medium if it's a channel thread, or to the URL
  // param if set, or to 'in_app' for unified/unknown threads.
  useEffect(() => {
    if (!selectedThread) return;
    // URL param takes priority (set by contact detail SMS/Email buttons)
    if (urlMedium === 'sms' || urlMedium === 'email') return;
    const pm = selectedThread.primaryMedium;
    if (pm === 'sms') setReplyMedium('sms');
    else if (pm === 'email') setReplyMedium('email');
    else setReplyMedium('in_app');
  }, [selectedThread?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get messages for selected thread (from separate query or from thread.messages)
  const threadMessages = useMemo(() => {
    if (messagesData?.messages) {
      return messagesData.messages;
    }
    if (selectedThread?.messages) return selectedThread.messages;
    return [];
  }, [messagesData, selectedThread]);

  // Get the reply-to message from cached messages (no extra API call)
  const replyToMessage = useMemo(() => {
    if (!replyToMessageId) return null;
    return threadMessages.find((m) => m.id === replyToMessageId) || null;
  }, [replyToMessageId, threadMessages]);

  // Clear reply-to when thread changes
  useEffect(() => {
    setReplyToMessageId(null);
  }, [selectedThreadId]);

  // Mark thread as read when selected
  useEffect(() => {
    if (selectedThreadId && selectedThread?.unreadCount && selectedThread.unreadCount > 0) {
      markThreadAsRead(selectedThreadId);
    }
  }, [selectedThreadId, selectedThread?.unreadCount, markThreadAsRead]);

  // Handle deep-link to specific message from URL params (e.g., notification click)
  useEffect(() => {
    if (urlMessageId && selectedThreadId && threadMessages.length > 0) {
      // Wait for messages to render, then scroll to and highlight the message
      const timeout = setTimeout(() => {
        const element = messageRefs.current.get(urlMessageId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedMessageId(urlMessageId);
          // Clear highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMessageId(null);
          }, 3000);
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [urlMessageId, selectedThreadId, threadMessages]);

  const handleArchiveThread = async (threadId: string) => {
    await updateThread({ id: threadId, isArchived: true });
    setSelectedThreadId(null);
  };

  const handleToggleFavorite = async (messageId: string, currentStatus: boolean) => {
    await toggleFavorite({ id: messageId, isFavorite: !currentStatus });
  };

  const handleSaveToFolder = async (messageId: string, folderId: string) => {
    await saveToFolder({ folderId, messageId });
    setShowFolders(false);
    setFolderMessageId(null);
  };

  // Handle reply send
  const handleSendReply = async () => {
    if (!selectedThread || !replyText.trim()) return;

    try {
      // Always use the user-selected replyMedium from the channel
      // selector. The selector is visible on all threads and defaults
      // to the appropriate medium based on thread type / URL param.
      const effectiveMedium: 'email' | 'sms' | 'in_app' = replyMedium;

      // In-app / chat messages
      if (effectiveMedium === 'in_app') {
        await sendChatMessage({
          threadId: selectedThread.id,
          bodyText: replyText,
          inReplyToMessageId: replyToMessageId || undefined,
        }).unwrap();
        setReplyText('');
        setReplyToMessageId(null);
        return;
      }

      // Resolve the to-address based on the selected medium
      let resolvedToAddress = '';
      const contact = (selectedThread as any).contact || selectedThread.primaryContact;
      if (effectiveMedium === 'sms') {
        resolvedToAddress = (contact as any)?.phone || selectedThread.externalAddress || '';
      } else if (effectiveMedium === 'email') {
        resolvedToAddress = contact?.email || selectedThread.externalAddress || '';
      }

      // Fallback: derive from last message
      if (!resolvedToAddress && threadMessages.length > 0) {
        const lastOutbound = [...threadMessages].reverse().find((m) => m.direction === 'outbound');
        const lastInbound = [...threadMessages].reverse().find((m) => m.direction === 'inbound');
        resolvedToAddress = lastOutbound?.toAddress || lastInbound?.fromAddress || '';
      }

      if (!resolvedToAddress) {
        console.error('No recipient address available for medium:', effectiveMedium);
        return;
      }

      const effectiveReplyToId =
        replyToMessageId ||
        (threadMessages.length > 0
          ? threadMessages[threadMessages.length - 1].id
          : undefined);

      await sendMessage({
        threadId: selectedThread.id,
        medium: effectiveMedium,
        toAddress: resolvedToAddress,
        subject: effectiveMedium === 'email'
          ? (selectedThread.subject ? `Re: ${selectedThread.subject}` : '(No subject)')
          : undefined,
        bodyText: replyText,
        emailProvider: effectiveMedium === 'email'
          ? (emailProviders?.[0]?.provider || undefined)
          : undefined,
        inReplyToMessageId: effectiveMedium === 'email'
          ? effectiveReplyToId
          : undefined,
      }).unwrap();
      setReplyText('');
      setReplyToMessageId(null);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Handle setting reply-to message
  const handleReplyToMessage = useCallback((messageId: string) => {
    setReplyToMessageId(messageId);
    // Focus the reply input (optional UX enhancement)
  }, []);

  // Handle compose email send
  const handleComposeSend = async () => {
    setComposeError(null);
    setComposeSuccess(false);

    if (!composeTo.trim()) {
      setComposeError('Recipient email is required');
      return;
    }
    if (!composeSubject.trim()) {
      setComposeError('Subject is required');
      return;
    }
    if (!composeBody.trim()) {
      setComposeError('Message body is required');
      return;
    }
    if (!hasEmailProvider) {
      setComposeError('No email provider configured. Connect Gmail or Outlook in Settings.');
      return;
    }

    setComposeSending(true);
    try {
      // Create (or reuse existing) thread — pass toAddress so the backend
      // can resolve the contact and avoid creating duplicate threads.
      const thread = await createThread({
        workspaceId: activeWorkspace?.workspace.id,
        subject: composeSubject,
        toAddress: composeTo.trim(),
        medium: 'email',
      }).unwrap();

      // Send the email via the thread
      await sendMessage({
        threadId: thread.id,
        medium: 'email',
        toAddress: composeTo,
        subject: composeSubject,
        bodyText: composeBody,
        emailProvider: emailProviders?.[0]?.provider || undefined,
      }).unwrap();

      // Clear form and show success
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      setComposeSuccess(true);
      setTimeout(() => setComposeSuccess(false), 3000);
    } catch (error) {
      const message = error && typeof error === 'object' && 'data' in error
        ? (error as { data?: { error?: string } }).data?.error || 'Failed to send email'
        : 'Failed to send email';
      setComposeError(message);
    } finally {
      setComposeSending(false);
    }
  };

  // Handle AI suggestion selection - pre-fill reply text
  const handleSelectSuggestion = (text: string) => {
    setReplyText(text);
  };

  // Handle jump to message from pinned board
  const handleJumpToMessage = useCallback((messageId: string) => {
    const element = messageRefs.current.get(messageId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(messageId);
      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    }
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // messages.read — the route guard, not just the menu.
  //
  // Hiding the sidebar entry is not authorization: `/messages` is reachable by
  // typing it, by an old `/emails` bookmark, and by any stored deep link. This
  // returns the fail-closed experience before any thread data renders.
  //
  // Deliberately checked AFTER the hooks above so hook order stays stable —
  // an early return placed before them would violate the rules of hooks.
  if (!canViewMessages) {
    return (
      <ErrorState
        title="Messages unavailable"
        message="You do not have permission to view messages in this workspace."
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Unibox</h1>
          <div className="flex items-center gap-2">
            {/* messages.write — both start a NEW thread, which the backend
                guards with messages.send. Hidden rather than disabled: there
                is no useful action a viewer could take in either dialog. */}
            {canWriteMessages && (
              <button
                onClick={() => setShowNewSmsModal(true)}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mediumIcons.sms} />
                </svg>
                New SMS
              </button>
            )}
            {canWriteMessages && (
              <button
                onClick={() => setShowNewChatModal(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mediumIcons.chat} />
                </svg>
                New Chat
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-4 border-b border-gray-200 -mb-px">
          <button
            onClick={() => setActiveTab('unibox')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'unibox'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Unibox
          </button>
          <button
            onClick={() => setActiveTab('weopen')}
            data-testid="unibox-tab-weopen"
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'weopen'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            WeOpen
          </button>
          <button
            onClick={() => setActiveTab('compose')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'compose'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Compose Email
          </button>
        </div>
      </header>

      {activeTab === 'unibox' ? (
        <div className="flex h-[calc(100vh-8rem)]">
          {/* Thread List Sidebar */}
          <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
            {/* Filters */}
            <div className="p-4 border-b border-gray-200 space-y-3">
              {/* Search */}
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search messages..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Primary Medium Filter Tabs */}
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {(['all', 'email', 'sms', 'chat'] as PrimaryMediumFilter[]).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setPrimaryMediumFilter(filter)}
                    className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      primaryMediumFilter === filter
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {filter === 'all' ? 'All' : filter === 'email' ? 'Email' : filter === 'sms' ? 'SMS' : 'Chat'}
                  </button>
                ))}
              </div>

              {/* Filter Toggles */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={unreadOnly}
                    onChange={(e) => setUnreadOnly(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  Unread only
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  Show archived
                </label>
              </div>
            </div>

            {/* Thread List */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingThreads ? (
                <div className="p-4 space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-100 rounded w-full" />
                    </div>
                  ))}
                </div>
              ) : threadsData?.threads.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <p>No messages found</p>
                </div>
              ) : (
                <ul>
                  {threadsData?.threads.map((thread) => {
                    const isUnified = thread.threadType === 'unified';
                    const contact = thread.primaryContact;
                    const threadContact = (thread as any).contact;
                    const lastMedium = thread.lastMessageMedium;

                    // Contact-first identity priority (same for ALL thread types):
                    // If a linked contact exists, always prefer contact details.
                    // 1. contact fullName
                    // 2. contact email
                    // 3. contact phone
                    // Only if no contact exists, fall back to thread-level fields:
                    // 4. externalAddress
                    // 5. subject (legacy threads only)
                    // 6. 'Conversation' / 'Unknown'
                    const contactName = contact?.fullName || threadContact?.fullName || null;
                    const contactEmail = contact?.email || threadContact?.email || null;
                    const contactPhone = contact?.phone || threadContact?.phone || null;
                    const hasLinkedContact = !!(contactName || contactEmail || contactPhone);

                    // Display name: contact identity first, then externalAddress.
                    // Subject is NEVER used as the primary title — it's content, not identity.
                    const displayName = contactName
                      || contactEmail
                      || contactPhone
                      || thread.externalAddress
                      || 'Unknown';
                    const initials = displayName.charAt(0).toUpperCase();

                    // Subtitle: show subject only when it adds info beyond the display name.
                    const showSubject = thread.subject && thread.subject !== displayName;

                    return (
                    <li key={thread.id}>
                      <button
                        onClick={() => setSelectedThreadId(thread.id)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          selectedThreadId === thread.id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                        } ${thread.unreadCount > 0 ? 'bg-blue-50/30' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                            isUnified ? 'bg-purple-100 text-purple-700' :
                            thread.primaryMedium === 'sms' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {initials}
                          </div>

                          <div className="flex-1 min-w-0">
                            {/* Row 1: Contact identity + time */}
                            <div className="flex items-center justify-between gap-2">
                              <p className={`truncate text-sm ${thread.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                                {displayName}
                              </p>
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {formatDate(thread.lastMessageAt)}
                              </span>
                            </div>
                            {/* Row 2 (legacy only): Subject line — only if distinct from display name */}
                            {showSubject && (
                              <p className={`text-sm truncate ${thread.unreadCount > 0 ? 'text-gray-700' : 'text-gray-600'}`}>
                                {thread.subject}
                              </p>
                            )}
                            {/* Row 3: Small medium badge + last message preview */}
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {lastMedium && (
                                <span className={`inline-flex items-center mr-1.5 text-[10px] font-medium px-1 py-0.5 rounded ${
                                  lastMedium === 'sms' ? 'bg-green-50 text-green-600' :
                                  lastMedium === 'email' ? 'bg-blue-50 text-blue-600' :
                                  lastMedium === 'chat' ? 'bg-purple-50 text-purple-600' :
                                  'bg-gray-50 text-gray-500'
                                }`}>
                                  {mediumLabels[lastMedium] || lastMedium}
                                </span>
                              )}
                              {thread.lastMessagePreview || 'No messages yet'}
                            </p>
                          </div>

                          {/* Unread badge */}
                          {thread.unreadCount > 0 && (
                            <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-blue-600 rounded-full">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Thread Detail */}
          <div className="flex-1 flex flex-col bg-white">
            {selectedThread ? (
              <>
                {/* Thread Header — contact-first, conversation-style */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Contact Avatar */}
                    {(() => {
                      const isUnified = selectedThread.threadType === 'unified';
                      const contact = selectedThread.primaryContact;
                      const threadContact = (selectedThread as any).contact;
                      // Same contact-first identity as thread rows
                      const contactName = contact?.fullName || threadContact?.fullName || null;
                      const contactEmail = contact?.email || threadContact?.email || null;
                      const contactPhone = contact?.phone || threadContact?.phone || null;
                      const headerName = contactName
                        || contactEmail
                        || contactPhone
                        || selectedThread.externalAddress
                        || 'Unknown';
                      // Secondary info: show a different detail than the header name
                      const secondaryInfo = (contactEmail && contactEmail !== headerName ? contactEmail : null)
                        || (contactPhone && contactPhone !== headerName ? contactPhone : null)
                        || (selectedThread.externalAddress && selectedThread.externalAddress !== headerName ? selectedThread.externalAddress : null)
                        || '';
                      // Only show secondary if it differs from the header name
                      const showSecondary = secondaryInfo && secondaryInfo !== headerName;
                      return (
                        <>
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                            isUnified ? 'bg-purple-100 text-purple-700' :
                            selectedThread.primaryMedium === 'sms' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {headerName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                              {headerName}
                            </h2>
                            <div className="flex items-center gap-2">
                              {/* Small medium badge */}
                              {selectedThread.primaryMedium && (
                                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${
                                  selectedThread.primaryMedium === 'sms' ? 'bg-green-50 text-green-600' :
                                  selectedThread.primaryMedium === 'email' ? 'bg-blue-50 text-blue-600' :
                                  selectedThread.primaryMedium === 'chat' ? 'bg-purple-50 text-purple-600' :
                                  'bg-gray-50 text-gray-500'
                                }`}>
                                  {mediumLabels[selectedThread.primaryMedium] || selectedThread.primaryMedium}
                                </span>
                              )}
                              {isUnified && (
                                <span className="inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">
                                  Unified
                                </span>
                              )}
                            </div>
                            {showSecondary && (
                              <p className="text-sm text-gray-500">{secondaryInfo}</p>
                            )}
                            {/* Show subject as tertiary metadata when it differs from the header identity */}
                            {selectedThread.subject && selectedThread.subject !== headerName && (
                              <p className="text-xs text-gray-400 truncate max-w-md">{selectedThread.subject}</p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* messages.write — archiving updates the SHARED thread
                        record, which the backend guards with threads.manage.
                        Central maps threads.manage to messages.write because
                        both carry {owner, admin, member}: members keep this
                        control, viewers do not get it. */}
                    {canWriteMessages && (
                    <button
                      onClick={() => handleArchiveThread(selectedThread.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Archive"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                        />
                      </svg>
                    </button>
                    )}
                  </div>
                </div>

                {/* Thread Collaboration Controls */}
                {activeWorkspace?.workspace.id && (
                  <div className="px-6 border-b border-gray-100">
                    <ThreadCollaborationHeader
                      threadId={selectedThread.id}
                      workspaceId={activeWorkspace.workspace.id}
                      lastMessageAt={selectedThread.lastMessageAt}
                      currentUserId={currentUser?.user?.id}
                      onJumpToMessage={handleJumpToMessage}
                    />
                  </div>
                )}

                {/* Collaboration Signals (typing indicators, on-call status) */}
                {activeWorkspace?.workspace.id && (
                  <CollaborationSignals
                    threadId={selectedThread.id}
                    workspaceId={activeWorkspace.workspace.id}
                  />
                )}

                {/* AI Summary and Suggestions */}
                <ThreadAISummary
                  threadId={selectedThread.id}
                  onSelectSuggestion={handleSelectSuggestion}
                  workspaceId={activeWorkspace?.workspace.id}
                />

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {threadMessages.map((message) => (
                    <div
                      key={message.id}
                      ref={(el) => {
                        if (el) messageRefs.current.set(message.id, el);
                      }}
                      className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'} ${
                        highlightedMessageId === message.id ? 'animate-pulse' : ''
                      }`}
                    >
                      <div
                        className={`max-w-lg rounded-lg p-4 transition-all duration-300 ${
                          message.direction === 'outbound'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        } ${
                          highlightedMessageId === message.id
                            ? 'ring-4 ring-yellow-400 ring-offset-2'
                            : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex items-center gap-2">
                            <svg
                              className={`w-4 h-4 ${
                                message.direction === 'outbound' ? 'text-blue-200' : 'text-gray-400'
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d={mediumIcons[message.medium]}
                              />
                            </svg>
                            <span
                              className={`text-xs ${
                                (selectedThread as any)?.threadType === 'unified'
                                  ? (message.medium === 'sms'
                                    ? 'px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium'
                                    : message.medium === 'email'
                                    ? 'px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium'
                                    : 'px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium')
                                  : (message.direction === 'outbound' ? 'text-blue-200' : 'text-gray-500')
                              }`}
                            >
                              {mediumLabels[message.medium]}
                            </span>
                          </div>
                          <span
                            className={`text-xs ${
                              message.direction === 'outbound' ? 'text-blue-200' : 'text-gray-400'
                            }`}
                          >
                            {formatDate(message.sentAt)}
                          </span>
                        </div>

                        {message.subject && (
                          <p className="font-medium mb-2">{message.subject}</p>
                        )}

                        {message.medium === 'email' ? (
                          // Display-only: show latest reply, hide quoted
                          // history (both directions). Stored body unchanged.
                          <EmailMessageBody
                            bodyText={message.bodyText}
                            bodyHtml={message.bodyHtml}
                            className="text-sm whitespace-pre-wrap"
                          />
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">
                            {message.bodyText || message.bodyHtml?.replace(/<[^>]*>/g, '') || ''}
                          </p>
                        )}

                        {/* Emoji Reactions */}
                        <div className="mt-2">
                          <MessageReactions
                            messageId={message.id}
                            reactions={message.reactions}
                            currentUserId={currentUser?.user?.id}
                            compact
                          />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-opacity-20 border-white">
                          <button
                            onClick={() => handleToggleFavorite(message.id, message.isFavorite)}
                            className={`p-1 rounded transition-colors ${
                              message.direction === 'outbound'
                                ? 'hover:bg-blue-500'
                                : 'hover:bg-gray-200'
                            }`}
                            title={message.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <svg
                              className={`w-4 h-4 ${
                                message.isFavorite
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : message.direction === 'outbound'
                                  ? 'text-blue-200'
                                  : 'text-gray-400'
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </button>

                          <button
                            onClick={() => {
                              setFolderMessageId(message.id);
                              setShowFolders(true);
                            }}
                            className={`p-1 rounded transition-colors ${
                              message.direction === 'outbound'
                                ? 'hover:bg-blue-500 text-blue-200'
                                : 'hover:bg-gray-200 text-gray-400'
                            }`}
                            title="Save to folder"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                              />
                            </svg>
                          </button>

                          {/* Pin Message */}
                          {activeWorkspace?.workspace.id && (
                            <PinMessageButton
                              messageId={message.id}
                              workspaceId={activeWorkspace.workspace.id}
                              className={
                                message.direction === 'outbound'
                                  ? 'hover:bg-blue-500 text-blue-200'
                                  : ''
                              }
                            />
                          )}

                          {/* Reply to this message */}
                          <button
                            onClick={() => handleReplyToMessage(message.id)}
                            className={`p-1 rounded transition-colors ${
                              message.direction === 'outbound'
                                ? 'hover:bg-blue-500 text-blue-200'
                                : 'hover:bg-gray-200 text-gray-400'
                            }`}
                            title="Reply to this message"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                              />
                            </svg>
                          </button>
                        </div>

                        {/* Quoted reply preview (if this message is a reply) */}
                        {message.inReplyToMessageId && (
                          <div
                            className={`mt-2 pt-2 border-t ${
                              message.direction === 'outbound'
                                ? 'border-blue-400 border-opacity-30'
                                : 'border-gray-200'
                            }`}
                          >
                            <div
                              className={`text-xs italic ${
                                message.direction === 'outbound'
                                  ? 'text-blue-200'
                                  : 'text-gray-500'
                              }`}
                            >
                              {(() => {
                                const quotedMsg = threadMessages.find((m) => m.id === message.inReplyToMessageId);
                                if (quotedMsg) {
                                  const senderName = quotedMsg.sender?.fullName || quotedMsg.fromAddress || 'Someone';
                                  const rawQuoted = quotedMsg.bodyText || '';
                                  // Display-only: preview the latest reply, not quoted history.
                                  const cleanedQuoted =
                                    quotedMsg.medium === 'email'
                                      ? stripQuotedEmailForDisplay(rawQuoted).visible || rawQuoted
                                      : rawQuoted;
                                  const snippet = cleanedQuoted.slice(0, 50);
                                  return (
                                    <span>
                                      Replying to {senderName}: "{snippet}{snippet.length >= 50 ? '...' : ''}"
                                    </span>
                                  );
                                }
                                return <span>Replying to message</span>;
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Box — messages.write.
                    The whole composer is withheld from a viewer rather than
                    rendered disabled: a reply box you cannot send from is
                    worse than no reply box, and every control inside it
                    (medium selector, quoted-reply cancel, Send) exists only
                    to produce a message the backend would 403. */}
                {canWriteMessages && (
                  isSystemChannelThread(selectedThread) ? (
                    /* System-channel (platform-owned) thread — the ordinary
                       medium-selector + Send composer is WRONG here: it calls
                       POST /api/unibox/messages/send, which never reaches the
                       certified system-channel reply route. Render the
                       dedicated composer instead and suppress the medium
                       selector entirely. Detection is by the canonical
                       `systemChannelId` field only (see isSystemChannelThread).
                       `recipientLabel` is display-only; the server derives the
                       real recipient. Authority is still enforced server-side. */
                    <SystemChannelReplyComposer
                      threadId={selectedThread.id}
                      recipientLabel={selectedThread.externalAddress ?? ''}
                      onReplyAccepted={refetchMessages}
                    />
                  ) : (
                <div className="border-t border-gray-200 p-4">
                  {/* Quoted reply preview above composer */}
                  {replyToMessageId && (
                    <div className="mb-3 p-2 bg-gray-50 border-l-4 border-blue-500 rounded flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-1">Replying to:</div>
                        <div className="text-sm text-gray-700 truncate">
                          {replyToMessage ? (
                            <>
                              <span className="font-medium">
                                {replyToMessage.sender?.fullName || replyToMessage.fromAddress || 'Message'}
                              </span>
                              {': '}
                              {(() => {
                                const raw = replyToMessage.bodyText || '';
                                const cleaned =
                                  replyToMessage.medium === 'email'
                                    ? stripQuotedEmailForDisplay(raw).visible || raw
                                    : raw;
                                return `${cleaned.slice(0, 80)}${cleaned.length > 80 ? '...' : ''}`;
                              })()}
                            </>
                          ) : (
                            <span className="italic">Message {replyToMessageId.slice(0, 8)}...</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setReplyToMessageId(null)}
                        className="ml-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                        title="Cancel reply"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {/* Channel selector — always visible so user can
                        explicitly choose how to send the next message.
                        Available options depend on the thread's contact. */}
                    <select
                      value={replyMedium}
                      onChange={(e) => setReplyMedium(e.target.value as 'email' | 'sms' | 'in_app')}
                      className={`px-2.5 py-2 border rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        replyMedium === 'sms' ? 'border-green-300 text-green-700' :
                        replyMedium === 'email' ? 'border-blue-300 text-blue-700' :
                        'border-gray-300 text-gray-700'
                      }`}
                      title="Choose how to send this message"
                    >
                      <option value="in_app">💬 In-App</option>
                      {(
                        (selectedThread as any)?.contact?.phone ||
                        (selectedThread?.primaryContact as any)?.phone ||
                        selectedThread?.primaryMedium === 'sms' ||
                        selectedThread?.externalAddress?.match(/^\+?\d/)
                      ) && (
                        <option value="sms">📱 SMS</option>
                      )}
                      {(
                        (selectedThread as any)?.contact?.email ||
                        selectedThread?.primaryContact?.email ||
                        selectedThread?.primaryMedium === 'email' ||
                        selectedThread?.externalAddress?.includes('@')
                      ) && emailProviders && emailProviders.length > 0 && (
                        <option value="email">✉️ Email</option>
                      )}
                    </select>
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => {
                        setReplyText(e.target.value);
                        if (e.target.value.trim()) {
                          startTyping();
                        }
                      }}
                      onBlur={stopTyping}
                      placeholder={`Type ${replyMedium === 'sms' ? 'an SMS' : replyMedium === 'email' ? 'an email' : 'a message'}...`}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={!replyText.trim() || isSending || isSendingChat}
                      className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSending || isSendingChat ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
                  )
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <p>Select a conversation to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'weopen' ? (
        /* WeOpen surface — reads the BFF live. Self-contained: shares no
           query, cache tag or component with the CRM thread panes above, and
           is intentionally absent from the All tab. */
        <WeOpenInboxPanel />
      ) : (
        /* Compose Tab */
        <div className="max-w-3xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Compose New Email</h2>

            {composeSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                Email sent successfully!
              </div>
            )}

            {composeError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                {composeError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="email"
                  placeholder="recipient@example.com"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  placeholder="Email subject"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  rows={12}
                  placeholder="Write your message..."
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-gray-500">
                  {hasEmailProvider
                    ? `Sending via ${emailProviders[0].provider === 'gmail' ? 'Gmail' : 'Outlook'}`
                    : ''}
                </div>
                <button
                  type="button"
                  onClick={handleComposeSend}
                  disabled={composeSending || !hasEmailProvider}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {composeSending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>

            {/* Email Provider Status */}
            {!hasEmailProvider && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Email not configured</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      To send emails, connect your Gmail or Outlook account.
                    </p>
                    <Link
                      to="/settings?tab=integrations"
                      className="inline-flex items-center gap-1 text-sm font-medium text-yellow-800 hover:text-yellow-900 mt-2"
                    >
                      Configure Email Provider
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Folder Selection Modal */}
      {showFolders && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Save to Folder</h3>
              <button
                onClick={() => {
                  setShowFolders(false);
                  setFolderMessageId(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {folders?.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => {
                    if (folderMessageId) {
                      handleSaveToFolder(folderMessageId, folder.id);
                    }
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-3"
                >
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                  <span className="font-medium text-gray-900">{folder.name}</span>
                  {folder.messageCount !== undefined && (
                    <span className="ml-auto text-sm text-gray-400">{folder.messageCount}</span>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={async () => {
                const name = window.prompt('Enter folder name:');
                if (name) {
                  await createFolder({ name, workspaceId: activeWorkspace?.workspace.id });
                }
              }}
              className="mt-4 w-full px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create New Folder
            </button>
          </div>
        </div>
      )}

      {/* New Chat Modal */}
      <NewChatModal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        workspaceId={activeWorkspace?.workspace.id}
        currentUserId={currentUser?.user?.id}
      />

      {/* New SMS Modal */}
      <NewSmsModal
        isOpen={showNewSmsModal}
        onClose={() => setShowNewSmsModal(false)}
      />
    </div>
  );
}
